import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumRegular,
    H5Emphasized,
    InfoRow,
    RowList,
    ScrollView,
    View,
    YStack,
} from 'smoothie';

import { getObjectKeyFromValue, isEmpty } from '../../../../common/misc';
import {
    DEFAULT_DATE_FORMAT_DASH,
    formatWithoutTimezone,
} from '../../../../common/utils/dateUtils';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import {
    READABLE_EMPLOYMENT_TYPE_MAP,
    READABLE_INDUSTRY_TYPE_MAP,
} from '../../../types/Edd';
import BankingTerms from '../../BankingTerms';
import KycButton from './KycButton';

interface ReviewProps {
    isInsideModal: boolean;
    kycData: RunKYCVerifyBody;
    onPressSubmit: () => void;
}

const Review = ({ isInsideModal, kycData, onPressSubmit }: ReviewProps) => {
    // terms are not relevant on this page when the flow is rendered in a modal
    const showTerms = !isInsideModal;
    const defaultTermsAccepted = isInsideModal; // If we're not showing terms assume terms are accepted

    const [termsAccepted, setTermsAccepted] = useState(defaultTermsAccepted);

    const {
        firstName,
        lastName,
        dateOfBirth,
        ssn,
        line1,
        line2,
        state,
        city,
        postalCode,
        employmentType,
        occupationIndustry,
    } = kycData;

    return (
        <ScrollView>
            <YStack flex={1} gap={16} justifyContent="space-between">
                <View>
                    <H5Emphasized marginBottom={8}>Review</H5Emphasized>
                    <BodyMediumRegular color="$gray1000" marginBottom={24}>
                        We&apos;ll use these details to confirm your identity.
                    </BodyMediumRegular>
                    <RowList>
                        <InfoRow
                            title="Name"
                            value={`${firstName} ${lastName}`}
                        />
                        <InfoRow
                            title="SSN"
                            value={`***-**-${(ssn ?? '').slice(-4)}`}
                            isPrivate={true}
                        />
                        <InfoRow
                            title="DOB"
                            value={
                                dateOfBirth != null
                                    ? formatWithoutTimezone(
                                          dateOfBirth,
                                          DEFAULT_DATE_FORMAT_DASH,
                                      )
                                    : ''
                            }
                            isPrivate={true}
                        />
                        <InfoRow
                            title="Address"
                            value={`${line1}${!isEmpty(line2) ? `, ${line2}` : ''}\n${city},${state != null ? ` ${state}` : ''} ${postalCode}`}
                            isPrivate={true}
                        />
                        {employmentType != null && (
                            <InfoRow
                                title="Employment status"
                                value={getObjectKeyFromValue(
                                    READABLE_EMPLOYMENT_TYPE_MAP,
                                    employmentType,
                                )}
                                isPrivate={true}
                            />
                        )}
                        {occupationIndustry != null && (
                            <InfoRow
                                title="Occupation"
                                value={getObjectKeyFromValue(
                                    READABLE_INDUSTRY_TYPE_MAP,
                                    occupationIndustry,
                                )}
                                isPrivate={true}
                            />
                        )}
                    </RowList>

                    {/* Disclosures must be presented on page that opens KYC in a modal */}
                    {showTerms && (
                        <BankingTerms
                            marginTop={24}
                            $mobile_mini={{ marginBottom: 4 }}
                            termsAccepted={termsAccepted}
                            onPress={() => {
                                setTermsAccepted((prev) => !prev);
                            }}
                            $sm={{ width: '95%', alignSelf: 'center' }}
                        />
                    )}
                </View>
                <KycButton
                    onPress={onPressSubmit}
                    text="Confirm"
                    disabled={!termsAccepted}
                />
            </YStack>
        </ScrollView>
    );
};

export default Review;
